import { idZodSchema } from "@@/shared/id";
import { initiatePayRequestCommandZodObject } from "@@/shared/payments/on-premise/commands/initiate-pay-request.command";
import { percentageZodSchema } from "@@/shared/percentage";
import { priceZodSchema } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { Except } from "type-fest";
import { z } from "zod";
import { newManagedPaymentIdFactory } from "./new-managed-payment-id";

const payRequestNewManagedPaymentZodObject = z
    .object({
        _id: idZodSchema,
        _type: z.literal("NEW_MANAGED_PAYMENT"),
        type: z.literal("PAY_REQUEST"),
        /** How many percent of the change total this replacement is replacing */
        percentage: percentageZodSchema.optional(),
        /** Total value of the new pay request in selected currency */
        value: priceZodSchema,
        command: initiatePayRequestCommandZodObject,
    })
    .refine(item => {
        // Make sure the value of the replacement matches the value of the pay request items
        const payReqTotalPrice = sumPrices(
            item.command.payRequestItems.map(payReqItem => payReqItem.price),
        );
        const amountsMatch =
            item.value.amountIncludingVat ===
            payReqTotalPrice.amountIncludingVat;
        return amountsMatch;
    }, `Replacement value and pay request summed value does not match`);
type PayRequestNewManagedPayment = z.infer<
    typeof payRequestNewManagedPaymentZodObject
>;

type FactoryParams = Except<
    PayRequestNewManagedPayment,
    "_id" | "_type" | "type"
>;
const payRequestNewManagedPaymentFactory = (params: FactoryParams) => {
    const replacement: PayRequestNewManagedPayment = {
        ...params,
        _id: newManagedPaymentIdFactory(),
        _type: "NEW_MANAGED_PAYMENT",
        type: "PAY_REQUEST",
    };
    return replacement;
};

export {
    payRequestNewManagedPaymentFactory,
    payRequestNewManagedPaymentZodObject,
};
export type { PayRequestNewManagedPayment };
